<template>
  <!--
    LESSON: v-html
    The beauty-box used Handlebars triple-braces {{{...}}} for raw HTML injection
    (sticker copy with <p> tags, etc.). In Vue the equivalent is v-html.
    Only use v-html for trusted CMS content — never for user-generated input.
  -->
  <section :id="sectionId" class="section-hero">
    <div v-if="heroData.html" v-html="heroData.html"></div>
    <template v-else>
      <div class="gift-card-rewards-container">
        <div
          class="bg-layer"
          v-lazy-bg="{ desktop: assets.backgroundImageDesktop, mobile: assets.backgroundImageMobile, duration: 500 }"
        ></div>
        <img v-if="assets.heroSticker" class="hero-sticker" :src="assets.heroSticker" alt="" />
        <div class="content-wrapper">
          <picture class="cards" v-lazy-load>
            <source media="(max-width:750px)" :data-srcset="assets.cardsMobileSource" />
            <img :data-src="assets.cardsDesktop" alt="" />
          </picture>
          <div class="text-content">
            <img class="logo" :srcset="assets.logoStarrewards" alt="Star Rewards">

            <h1 v-if="!heroData.h1_mobile" v-html="heroData.h1"></h1>
            <template v-else>
              <h1 class="h1-desktop" v-html="heroData.h1"></h1>
              <h1 class="h1-mobile" v-html="heroData.h1_mobile"></h1>
            </template>
            <h2 v-html="heroData.h2"></h2>
            <p v-html="heroData.body"></p>

          </div>
        </div>

        <p class="legal" v-html="heroData.legal"></p>
      </div>
    </template>
  </section>
</template>

<script>
import { fadeInOnScrollDirective } from '../utils/fadeInOnScroll';
import { lazyLoad } from '../utils/lazyLoad';
import { lazyBg } from '../utils/lazyBg';
import config from 'config';
import externalAssetsMixin from '../mixins/externalAssets';

const BASE_ASSETS = config.baseAssetsUrl;

export default {
  name: 'SectionHero',

  mixins: [externalAssetsMixin],

  directives: {
    fadeIn: fadeInOnScrollDirective,
    lazyLoad: {
      mounted(el, binding) {
        const { slideY = '0' } = binding.value || {};
        el.style.opacity = '0';
        el.style.transform = `translateY(${slideY})`;
        lazyLoad(el, binding.value);
      },
    },
    lazyBg: {
      mounted(el, binding) { lazyBg(el, binding.value); },
    },
  },

  props: {
    heroData: {
      type: Object,
      required: true,
    },
  },

  computed: {
    sectionId() {
      return this.heroData.id || 'hero';
    },

    assets() {
      const img = this.heroData.images;
      const base = `${BASE_ASSETS}/${this.heroData.image_folder || 'section-hero'}`;
      return {
        backgroundImageDesktop: `${base}/${img.background_desktop}`,
        backgroundImageMobile:  `${base}/${img.background_mobile}`,
        logoStarrewards:        `${base}/${img.logo_starrewards}`,
        cardsDesktop:           `${base}/${img.cards_desktop}`,
        cardsMobile:            `${base}/${img.cards_mobile}`,
        cardsMobileSource:      `${base}/${img.cards_mobile}`,
        logoMobile:             `${base}/${img.logo_mobile}`,
        logoDesktop:            `${base}/${img.logo_desktop}`,
        heroMobile:             `${base}/${img.hero_mobile}`,
        heroDesktop:            `${base}/${img.hero_desktop}`,
        heroSticker:            img.hero_sticker ? `${base}/${img.hero_sticker}` : null,
      };
    },
  },

};
</script>

<style lang="scss">
/* Exact port of beauty-box/dev/scss/page_index/section_hero.scss */
#campaign section.section-hero {

  .gift-card-rewards-container {
    position: relative; 
    max-width: 1600px;
    width: 100%;
    height: 575px;
    background-color: v-bind('heroData.color');
    margin: 0 auto;
    border: 1px solid v-bind('heroData.color');
    overflow: hidden;
    container-type: inline-size;
    display: flex;
    justify-content: center;

     @media (min-width: 750px) and (max-width: 1600px) {
      height: 35.9375cqi;
    }

    @media (max-width: 749px) {
        height: auto;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }

    .bolder {
      font-family: 'Macys Sans Medium', sans-serif;
    }

    .bg-layer {
      position: absolute;
      inset: 0;
      background-size: cover;
      background-position: center;
      z-index: 0;
    }

    .content-wrapper {
        position: relative;
        display: flex;
        flex-direction: row;
        overflow: hidden;

        @media (max-width: 749px) {
          flex-direction: column;
          height: 100%;
          /* background-image set via v-lazy-bg */
        }


      .cards {
        top: -86px;
        position: relative;
        width: 678px;
        max-width: 678px;
        max-height: 566px;
        aspect-ratio: 678 / 566;
        margin: 0 0 0 0;
        @media (min-width: 750px) and (max-width: 1600px) {
          top: -5.375cqi;
          width: 42.375cqi;
        }
        @media (max-width: 749px) {
          max-width: none;
          max-height: none;
          width: 80cqi;
          aspect-ratio: 786 / 1100;
          margin: 40cqi -8cqi 0 auto;
          /* background-image set via v-lazy-bg */
        }
      }

      .text-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        width: 530px;
        margin: 0 0 50px 0;

        @media (min-width: 750px) and (max-width: 1600px) {
          width: 33.3125vw; /* 501 / 1600 * 100 */
          margin: 0 0 3.125vw 0; /* 50 / 1600 * 100 */
        }
        @media (max-width: 749px) {
          width: 100%;
          margin: -18vw 0 4vw 0;
          padding: 0 0 0 6.4cqi;
        }
        
        .logo {
          display: inline-block;
          width: 175px;
          height: 21px;
          @media (min-width: 750px) and (max-width: 1600px) {
            width: 10.9375cqi;
            height: 1.3125cqi;
          }
          @media (max-width: 749px) {
            width: 36.26666cqi;
            height: 4.53333cqi;
          }
        }
        h1 {
          font-size: 51px;
          line-height: 1.1;
          font-family: 'Ramillas Medium', serif;
          letter-spacing: -0.01em;
          margin: 21px 0 0 0;

         

          @media (min-width: 750px) and (max-width: 1600px) {
            font-size: 3.1875vw;
            margin: 1.3125vw 0 0 0;
          }
          @media (max-width: 749px) {
            font-size: 9cqi;
            margin: 2.4cqi 0 0 0;
          }
        }

        h2 {
          font-size: 31px;
          line-height: 1.1;
          font-family: 'Macys Sans Medium', sans-serif;
          letter-spacing: -0.03em;

          @media (min-width: 750px) and (max-width: 1600px) {
            font-size: 1.9375vw;
          }
          @media (max-width: 749px) {
            font-size: 5.33333cqi;
          }
        }

        p {
          margin: 16px 0 0 0;
          font-family: 'Macys Sans Regular', sans-serif;
          line-height: 1.3;
          font-size: 15px;

          @media (min-width: 750px) and (max-width: 1600px) {
            font-size: 0.9375vw; /* 15 / 1600 * 100 */
            margin: 1vw 0 0 0;
          }

          @media (max-width: 749px) {
            /* Adjust 1cqi up or down to change how fast it grows */
            font-size: 3.2cqi;
          }
        }

        a{
          text-decoration: underline;
        }
      }
    }

    .legal {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translate(-50%, 0);
      width: 1057px;
      max-width: 90%; /* Increased slightly for better scaling */
      text-align: center;
      font-family: 'Macys Sans Medium', sans-serif;
      line-height: 1.1;

      /* Mobile Default */
      @media (max-width: 749px) {
        padding: 9vw 0 0 0;
        position: relative;
        margin: 0 auto;
        font-size: 10px;
        left: unset;
        bottom: 5vw;
        transform: unset;
      }

      /* Desktop Fluid */
      @media (min-width: 750px) {
        /* Adjust 1cqi up or down to change how fast it grows */
        font-size: clamp(8px, 1cqi, 10px);
      }
    }

  }
  .h1-mobile { display: none; }

  @media (max-width: 749px) {
    .h1-desktop { display: none; }
    .h1-mobile { display: block; }
  }

  .inner {
    padding: 0;
    max-width: 1440px;
  }

  .subtop {
    background-color: #000;
    padding: max(1.6cqi, 20px) 0;
    margin-bottom: 3cqi;
  }
  .subtop .logo {
    width: 27cqi;
    margin: auto;
  }

  .hero-img-wrapper {
    display: grid;
    grid-template-areas: 'container';
    position: relative;

    > * {
      grid-area: container;
    }
  }

  .subbottom {
    max-width: 62cqi;
    margin: auto;
    text-align: center;
    container-type: inline-size;
    padding: 4cqi 0.5cqi;
  }

  .subbottom h2 {
    font: 4cqi / 1.2 'Ramillas Regular',serif;
  }

  .subbottom p {
    font: 2.1cqi / 1 'Macys Sans Bold', sans-serif;
    padding-top: 1.7cqi;
  }

  .link {
    font-family: 'Macys Sans Medium', sans-serif;
  }

  .cta-button {
    background-color: var(--subscribe-cta-bg);
    border-radius: 8px;
    border: 1px solid #cecece;
    position: absolute;
    bottom: 9%;
    transition: background-color 0.5s ease;
    width: 13cqi;
    height: 3.7cqi;
    line-height: 3.7cqi;
    text-align: center;

    span {
      display: block;
      font-size: 9.5cqi;
      font-family: 'Macys Sans Bold', sans-serif;
      color: var(--subscribe-cta-font-color);
      transition: color 0.5s ease;
    }

    &:hover {
      background-color: var(--subscribe-cta-font-color);

      span {
        color: var(--subscribe-cta-bg);
      }
    }
  }

}

#campaign section#hero {
  @media (max-width: 749px) {
    .gift-card-rewards-container {
      aspect-ratio: 768 / 1219;
    }

    .content-wrapper {
      width: 100%;
      height: 100%;
    }

    .text-content {
      margin: 67cqi 0 0 5cqi;
      padding: 0;
    }

    .cards {
      position: absolute;
      top: 0;
      right: 0cqi;
      width: 92cqi;
      margin: 0;
      pointer-events: none;
    }
  }
}

#campaign section#hero-jun2026 .hero-sticker {
  position: absolute;
  top: -10px;
  left: calc(50% - 104px);
  transform: translateX(-50%);
  width: 120px;
  z-index: 1;
  pointer-events: none;

  @media (min-width: 750px) and (max-width: 1600px) {
    top: -0.625vw;
    left: calc(50% - 6.5vw);
    width: 7.5vw;
  }

  @media (max-width: 749px) {
    top: 3.2cqi;
    left: calc(50% - -25.27cqi);
    width: 28cqi;
  }
}

#campaign section#hero-jun2026 .content-wrapper .cards {
  @media (min-width: 750px) {
    top: 0;
  }
}

#campaign section#hero-jun2026 .legal {
  font-family: 'Macys Sans Regular', sans-serif;
  font-weight: 400;
  letter-spacing: -0.02em;

  @media (min-width: 1600px) {
    bottom: 16px;
    font-size: 14px;
    width: 1280px;
    letter-spacing: -0.05em;
  }

  @media (min-width: 750px) and (max-width: 1599px) {
    bottom: 1vw;
    font-size: clamp(10px, 0.875vw, 14px);
    width: 80vw;
    letter-spacing: -0.05em;
  }
}

#campaign section#hero-jun2026 .text-content {
  width: 650px;

  @media (min-width: 750px) and (max-width: 1600px) {
    width: 40.625vw;
  }
}

#campaign section#hero-jun2026 .text-content p {
  font-family: 'Macys Sans Regular', sans-serif;
  font-weight: 400;
}

#campaign section#hero-jun2026 .bolder {
  font-family: 'Macys Sans Regular', sans-serif;
  font-weight: 700;
}

#campaign section#hero-jun2026 .shop-cta {
  font-family: 'Macys Sans Medium', sans-serif;

  @media (min-width: 750px) {
    font-size: clamp(12px, 0.875vw, 14px);
  }
}

#campaign section#hero-jun2026 p {
  @media (min-width: 750px) {
    font-size: clamp(10px, 0.75vw, 12px);
  }
}

#campaign section#hero-jun2026 .gift-card-rewards-container {
  @media (min-width: 1600px) {
    height: 587px;
  }

  @media (min-width: 750px) and (max-width: 1599px) {
    height: 36.6875vw;
  }
}

#campaign section#hero-jun2026 p br.mobile-only {
  @media (min-width: 376px) and (max-width: 749px) {
    display: inline !important;
  }
}

#campaign section#hero-jun2026 p br.desktop-only {
  display: inline;
  @media (max-width: 972px) {
    display: none;
  }
}

#campaign section#hero-jun2026 {
  h2 { display: none; }
  h1 { line-height: 1; }

  @media (max-width: 749px) {

    .gift-card-rewards-container {
      position: relative;
      aspect-ratio: 375 / 545;
    }

    .content-wrapper {
      width: 100%;
      height: 100%;
    }

    .desktop-only {
      display: none;
    }

    .text-content {
      order: -1;
      width: 78cqi;
      margin: 5cqi 0 4cqi 3cqi;
      padding: 0;

      h1, h2, p {
        padding-left: 10cqi;
      }

      .logo { margin-left: 2.5vw; margin-top: 2.33vw; }
      h1 { margin-top: 12cqi; line-height: 0.95; }
      p { margin-top: 4cqi; font-size: 2.67vw; }
      .shop-cta { font-size: 3.73vw; }
    }

    .cards {
      position: absolute;
      right: 0;
      top: auto;
      bottom: 24cqi;
      width: 58cqi;
      margin: 0;
    }

    .legal {
      position: absolute;
      bottom: 11.5cqi;
      left: 50%;
      transform: translateX(-50%);
      width: 75%;
      padding: 0;
      font-size: 2.2cqi;
      line-height: 1.3;
      text-align: justify;
    }

    .shop-cta {
      font-family: 'Macys Sans Medium', sans-serif;
      text-decoration: underline;
    }
  }
}
</style>
